import {Subject} from 'rxjs';
import {ResizeObservable} from '@shared/util/dom/resize.observable';
import {afterNextRender, AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewRef} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors} from '@angular/forms';
import {DatatableComponent, TableColumn as SwimLaneTableColumn, SelectionType as SwimLaneSelectionType, ColumnMode as SwimLaneColumnMode, SortDirection as SwimLaneSortDirection} from '@swimlane/ngx-datatable';
import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {I18nMessage} from '@shared/api/model/i18n';
import {BootstrapService, BootstrapBreakpoint, ElementIdGenerationService, ElementIdGenerator} from '../../services';
import {UserProfileService} from '@shared/services/api/user-profile.service';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {LoggedInUser} from '@shared/services/api/logged-in-user';
import {DEFAULT_PAGE_SIZE_OPTIONS} from '../ace-pagination/ace-pagination.component';


export interface PageInfo {
  count?: number;
  pageSize?: number;
  limit?: number;
  offset?: number;
  rows?: number;
  first?: number;
}

export interface PageEventExtras {
  [key: string]: any;
}

export interface PageEvent extends PageInfo {
  extras?: PageEventExtras;
}

export interface AceTableI18nMessages {
  emptyMessage?: I18nMessage;
  totalMessage?: I18nMessage;
  selectedMessage?: I18nMessage;
}

export import SortDirection = SwimLaneSortDirection;

export interface TableColumn extends Omit<SwimLaneTableColumn, 'name'> {
  name?: string | I18nMessage;
  defaultSortDir?: SortDirection;
  comparator?: DataTableComparator<any, any>;
}

/** The narrowest a resizable column should be set to. ngx-datatable applies no floor of its own, so without one, a
 * drag can collapse a column to nothing and leave no handle to drag it back. A column takes `AceTableComponent`'s
 * wider default unless this is applied. */
export const MIN_RESIZABLE_COLUMN_WIDTH = 50;

export import SelectionType = SwimLaneSelectionType;
export enum CustomColumnMode {
  WORKAROUND_FLEX = 'workaround_flex'
}
export import ColumnMode = SwimLaneColumnMode;


export interface DataTableSortOption<K extends string = string> {
  prop: K;
  dir: SortDirection;
}

export type DataTableComparator<T, R> = (valueA: T, valueB: T, rowA: R, rowB: R, sortDirection: SortDirection) => number;

export interface SortEvent<SortOption extends DataTableSortOption = DataTableSortOption> {
  column: TableColumn;
  newValue: SortDirection;
  prevValue: SortDirection | null;
  sorts: SortOption[];
}

@Component({
  selector: 'app-ace-table',
  templateUrl: './ace-table.component.html',
  styleUrls: ['./ace-table.component.scss'],
  standalone: false
})
export class AceTableComponent implements AfterViewChecked, OnChanges, OnInit, OnDestroy {

  // currently in datatable-pager element, the max pager size is defined internally as 5
  // only showing 5 selectable pages from current page.
  private readonly MAX_PAGER_SIZE = 5;
  private readonly BREAKPOINT_ALL = 'all';
  // Room for about ten characters, so most words fit on a line and wrap whole. Narrower than this and `text-break`
  // starts splitting them mid-character
  private readonly DEFAULT_COLUMN_MIN_WIDTH = 100;

  readonly idGenerator: ElementIdGenerator;

  @ViewChild('tableWrapper', {static: true}) tableWrapper;
  @ViewChild(DatatableComponent, {static: true}) table: DatatableComponent;
  @Input() rowIdentity: (rowItem: any) => any;
  @Input() columns: TableColumn[] = [];
  /** There is a bug with `ColumnMode.flex` where resizing does not sync whole column to the header size.
   * The workaround is to set to `ColumnMode.force` after the next render. We allow a custom column mode here so
   * that we know when we have to use the workaround which we apply on `actualColumnMode`.
   * Github bug: https://github.com/swimlane/ngx-datatable/issues/919 */
  @Input() columnMode: ColumnMode | CustomColumnMode;
  /** Only valid together with `CustomColumnMode.WORKAROUND_FLEX` (setting it with any other column mode throws).
   * The flex -> force flip that locks in the proportional `flexGrow` widths is deferred while this is `false` and runs
   * once it is `true`, freezing the columns at their proportional widths rather than equalising them. Set it to
   * `false` while the proportions cannot be worked out yet: while the column set is still being assembled (e.g.
   * columns derived from data that is still loading), or while the table is hidden, as a hidden table measures 0px
   * wide and its columns would lock in at their minimum widths. Leave as `null` (the default) for `WORKAROUND_FLEX`
   * tables whose columns are static and which are visible when they first render - they flip to force as soon as
   * they render. */
  @Input() columnsFinalized: boolean | null = null;
  @Input() count: number;
  @Input() externalPaging = false;
  @Input() externalSorting = false;
  @Input() footerHeight = 50;
  @Input() headerHeight = 50;
  @Input() loading = false;
  @Input() loadingIndicatorOverBodyOnly = false;
  @Input() offset = 0;
  @Input() rowClass: any;
  @Input() limit;
  @Input() reorderable = true;
  @Input() rowHeight = 'auto';
  @Input() scrollbarV = false; // false is the default in ngx-datatable
  @Input() scrollbarH = false; // false is the default in ngx-datatable
  @Input() rows: any = [];
  @Input() selected: any = [];
  @Input() selectionType: SelectionType = null;
  @Input() selectCheck: any;
  @Input() simplePaging = false;
  @Input() responsivity: {widthFrom: 'screen' | 'table', breakpoint: BootstrapBreakpoint | 'all', leeway?: number};
  @Input() showPageLimitControl = true;
  @Input() pageLimitOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  @Input() loadDataOnInit = false;
  @Input() customCssClass;
  @Input() messages: AceTableI18nMessages;
  @Input() sorts;
  @Input() showJumpToPage = true;
  @Input() summaryRow: boolean;
  @Input() summaryPosition: any;
  @Input() summaryHeight: number;
  @Input() defaultSortDirection: SortDirection;
  @Input() stripedRows = true;

  @Output() page: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
  @Output() selectEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() activate: EventEmitter<any> = new EventEmitter<any>();
  @Output() sort: EventEmitter<SortEvent> = new EventEmitter();

  private readonly TABLE_SPINNER_CSS_CLASS_NAME = 'ace-table-spinner';
  private readonly TABLE_ON_TOP_CSS_CLASS_NAME = 'on-top';
  private readonly TABLE_SPINNER_BOUNCE_CSS_CLASS_NAME_PREFIX = 'bounce';
  private readonly logger: AceLogger;

  private defaultTableRowIdentityFn: (rowItem: any) => any;
  private columnDefaultSortDirs = new Map<string, SortDirection>();
  // Used to indicate that columns have been updated and hence columnDefaultSortDirs should be rebuilt. The keys in
  // columnDefaultSortDirs are column ids ($$id property of a TableColumn), which are generated by ngx datatable.
  // Therefore, before columnDefaultSortDirs is rebuilt, the $$id property of each column needs to be initialised. Even
  // though at the time of writing the ngx datatable uses a setter instead of its own ngOnChanges() to initialise
  // columns and their $$id property, which makes the $$id property of each column available to the ngOnChanges() method
  // of this component, to be safe in case it changes in the future, this behaviour is not relied upon.
  // The columnDefaultSortDirs is rebuilt in ngAfterViewChecked() of this component when rebuildDefaultColumnSortDirs is
  // true
  private rebuildDefaultColumnSortDirs = false;
  // Guards against scheduling more than one pending flex -> force flip for the WORKAROUND_FLEX column mode.
  private flexToForceFlipScheduled = false;
  // private breakPointConfig: {name: string, leeway: number};
  private ngUnsubscribe = new Subject<void>();

  tableHTMLId: string;
  jumpToPage: UntypedFormGroup;
  jumpToPageElementId: string;
  rowClassFn: any;
  tableClass = '';
  actualColumnMode: ColumnMode = ColumnMode.force;

  constructor(
    private cdRef: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    protected injector: Injector,
    private bootstrapService: BootstrapService,
    private userProfileService: UserProfileService,
    idGenerationService: ElementIdGenerationService,
    logService: AceLoggerService
  ) {
    this.logger = logService.create(AceTableComponent.name);
    // Need to initialise this variable early, (i.e. in constructor instead of in ngOnInit() so that the variable is available
    // to the parent component as soon as this component is constructed
    if (!this.limit) {
      this.limit = AceTableComponent.getRowsPerPageForUser(this.userProfileService.getLoggedInUser());
    }

    if (this.pageLimitOptions && !this.pageLimitOptions.includes(this.limit)) {
      this.addCurrentLimitToPageLimitOption();
    }

    this.idGenerator = idGenerationService.buildIdGenerator();
    this.tableHTMLId = this.idGenerator.getId('main-table-id');
    this.jumpToPageElementId = this.idGenerator.getId('jump-to-page');
  }

  /*
   * This is a work around due to an Angular limitation/bug: https://github.com/angular/angular/issues/15634
   *
   * The problem is that Angular does not allow to create new views during a change detection cycle. A modal (dynamically) creates a new
   * view which causes issues during change detection.
   *
   * The problem is discussed from ngx-datatable point of view here:
   * https://github.com/swimlane/ngx-datatable/issues/721#issuecomment-390993311
   */
  static fixExpressionChangedBug(event) {
    if (event && event.target) {
      event.target.closest('datatable-body-cell').blur();
    }
  }

  static getRowsPerPageForUser(user: LoggedInUser): number {
    const rowsPerPage = user && user.preferences.table_options.rows_per_page;
    return rowsPerPage ? rowsPerPage : 10;
  }

  get maxPageNum(): number {
    return Math.ceil(this.count / this.table.pageSize);
  }

  get currentPage(): number {
    // table offset starts from 0
    return this.table.offset + 1;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngOnInit(): void {
    this.defaultTableRowIdentityFn = this.table.rowIdentity;
    this.checkAndResetRowIdentityFn();

    if (this.loadDataOnInit) {
      this.pageEvent({offset: this.offset, limit: this.limit});
    }

    this.jumpToPage = this.fb.group({
      pageNumber: new UntypedFormControl(this.offset + 1, {validators: [this.pageNumberValidator.bind(this)]})
    });
    this.listenForTableResizeEvents();
  }

  pageNumberValidator(c: AbstractControl): ValidationErrors {
    const value: number = +c.value;
    if (value < 0 || value > this.maxPageNum || isNaN(value)) {
      return {invalidPageNumber: {messageKey: 'Page number should be in range', parameters: {startPage: 1, endPage: this.maxPageNum }}};
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rowIdentity) {
      this.checkAndResetRowIdentityFn();
    }

    if (changes.pageLimitOptions) {
      if (changes.pageLimitOptions.currentValue && Array.isArray(changes.pageLimitOptions.currentValue) &&
        !changes.pageLimitOptions.currentValue.includes(this.limit)) {
        this.addCurrentLimitToPageLimitOption();
      }
    }

    if (changes.rows && this.simplePaging) {
      if (changes.rows.currentValue) {
        this.count = this.offset * this.limit + changes.rows.currentValue.length;
      } else {
        this.count = 0;
      }

      this.table.count = this.count;
    }

    if (changes.columns) {
      (this.columns || []).forEach(c => {
        // want the default value for sortable to be false
        if (c.sortable == null) {
          c.sortable = false;
        }
        if (c.minWidth == null) {
          c.minWidth = this.DEFAULT_COLUMN_MIN_WIDTH;
        }
      });
      this.rebuildDefaultColumnSortDirs = true;
    }

    if (changes.rowClass) {
      // if rowClass is a string convert to a function
      this.rowClassFn = typeof this.rowClass === 'string' ? () => this.rowClass : this.rowClass;
    }

    if (changes.columnMode || changes.columns || changes.columnsFinalized) {
      this.updateActualColumnMode(changes);
    }
  }

  ngAfterViewChecked() {
    this.checkLoadingIndicatorOverBodyOnly();
    this.rebuildDefaultColumnSortDirsIfNeeded();
  }

  pageEvent(pagingEvent: PageInfo, extras?: PageEventExtras) {
    this.offset = pagingEvent.offset;
    this.limit = pagingEvent.limit;
    this.page.emit(extras != null ? {...pagingEvent, extras} : pagingEvent);
  }

  onSelectEvent(selectEvent: any) {
    this.selectEvent.emit(selectEvent);
  }

  activateEvent(activateEvent: any) {
    this.activate.emit(activateEvent);
  }

  sortEvent(sortEvent: any) {
    if (!this.resortIfNecessary(sortEvent)) {
      this.sort.emit(sortEvent);
    }
  }

  private checkAndResetRowIdentityFn() {
    if (this.rowIdentity == null) {
      this.rowIdentity = this.defaultTableRowIdentityFn;
    }
  }

  /**
   * `ColumnMode.flex` distributes width by `flexGrow` but discards manual resizes (ngx-datatable bug #919),
   * whereas `ColumnMode.force` keeps manual resizes but distributes leftover width equally, ignoring `flexGrow`.
   * For `CustomColumnMode.WORKAROUND_FLEX` we get both: stay in `flex` so `flexGrow` lays the columns out
   * proportionally, then once the final column set exists (`columnsFinalized`) freeze to `force` - which preserves
   * the proportional widths it inherits and makes the columns resizable. The flex layout is re-armed whenever the
   * column set rebuilds so a later change (e.g. a new date range) recomputes proportions instead of equalising the
   * new columns.
   */
  private updateActualColumnMode(changes: SimpleChanges): void {
    if (changes.columnsFinalized && this.columnsFinalized != null && this.columnMode !== CustomColumnMode.WORKAROUND_FLEX) {
      throw new Error('columnsFinalized is only applicable when columnMode is CustomColumnMode.WORKAROUND_FLEX');
    }
    if (this.columnMode === CustomColumnMode.WORKAROUND_FLEX) {
      if (changes.columnMode || changes.columns) {
        this.actualColumnMode = ColumnMode.flex;
      }
      // only an explicit false defers the flip until the columns are final
      // if the flag is not set, always treat it as finalized whenever columns change
      if (this.columnsFinalized !== false) {
        this.scheduleFlexToForceFlip();
      }
    } else if (changes.columnMode) {
      this.actualColumnMode = this.columnMode;
    }
  }

  private scheduleFlexToForceFlip(): void {
    if (!this.flexToForceFlipScheduled) {
      this.flexToForceFlipScheduled = true;
      afterNextRender(() => {
        this.flexToForceFlipScheduled = false;
        // On a full refresh the first flex layout can run before the sidebar takes its width, sizing the columns for a
        // wider container than they get. Force saves the widths it first sees and, on a table that scrolls sideways,
        // restores them whenever the columns overflow, so that mistake would stick. Re-running flex here re-shares the
        // real width before force saves anything.
        this.table.recalculate();
        // The columns have already been laid out proportionally by flexGrow in flex mode (the columnMode-before-columns
        // binding order ensures the columns rebuild recalculates in flex). Switching to force preserves those widths -
        // force only redistributes leftover width, which is ~0 once flex has filled the table - and makes the columns
        // resizable, which flex mode does not allow (ngx-datatable bug #919).
        this.actualColumnMode = ColumnMode.force;
        this.cdRef.detectChanges();
        this.table.recalculate();
        // The flip changes the column widths, so bring the scroller total back in step.
        this.syncScrollerWidthToColumns();
      }, {injector: this.injector});
    }
  }

  private resortIfNecessary(sortEvent: any): boolean {
    // If external sorting is enabled, do not do anything as all of the sorting will be handled externally
    // sortEvent.prevValue is not set when the column was not active in the sort before this event
    if (!this.externalSorting && !sortEvent.prevValue && sortEvent?.sorts?.length) {
      const sortDir = this.columnDefaultSortDirs.get(sortEvent.column.$$id) || this.defaultSortDirection;
      if (sortDir && sortEvent.newValue !== sortDir) {
        // ngx datatable supports sorting by multiple columns. Use the same approach it uses to find the column in sorts:
        // eslint-disable-next-line max-len
        // https://github.com/swimlane/ngx-datatable/blob/f870a877a88c3760e84748660933e92c98defe38/projects/swimlane/ngx-datatable/src/lib/components/header/header.component.ts#L291
        const index = sortEvent.sorts.findIndex(s => s.prop === sortEvent.column.prop);
        if (index >= 0) {
          const newEvent = {...sortEvent, newValue: sortDir};
          newEvent.sorts[index].dir = sortDir;
          this.table.onColumnSort(newEvent);
          return true;
        }
      }
    }

    return false;
  }

  private addCurrentLimitToPageLimitOption() {
    this.pageLimitOptions.push(this.limit);
    this.pageLimitOptions.sort((a, b) => a - b);
  }

  private resizeTableIfNeeded(): void {
    if (this.table && !(this.cdRef as ViewRef).destroyed) {
      if (typeof this.table.recalculate === 'function') {
        this.calculateTableClass(this.tableWrapper.nativeElement.clientWidth);
        this.syncScrollerWidthToColumns();
        this.cdRef.detectChanges();
      } else {
        this.logger.error('Cannot adjust table width as the "recalculate()" function is missing.');
      }
    }
  }

  /** ngx-datatable sizes the scroller and header from `columnGroupWidths`, which `recalculate()` leaves stale: only
   * replacing the columns array updates it (https://github.com/swimlane/ngx-datatable/issues/455). `onColumnResize`
   * replaces the array, so re-applying the first column's width refreshes the total. Without it the table scrolls
   * into room reserved for widths the columns no longer have, which only a scrolling table can show. */
  private syncScrollerWidthToColumns(): void {
    if (this.scrollbarV || this.scrollbarH) {
      try {
        const cols = this.table.columns;
        if (cols && cols.length) {
          const col = cols[0];
          this.table.onColumnResize({column: col, newValue: col.width});
          this.table.recalculate();
        }
      } catch (e) {
        this.logger.error('Error while trying to adjust datatable size', e);
      }
    }
  }

  private calculateTableClass(width: number): void {
    this.tableClass = this.responsivity?.widthFrom === 'screen' && this.applyResponsiveClass(width)
      ? 'datatable-responsive-' + this.responsivity.breakpoint
      : this.responsivity?.widthFrom === 'table' && (this.responsivity.breakpoint === this.BREAKPOINT_ALL || this.applyResponsiveClass(width))
        ? 'datatable-responsive'
        : '';
  }

  private applyResponsiveClass(width: number): boolean {
    const breakpoint = this.responsivity?.breakpoint;
    if (breakpoint && breakpoint !== 'all') {
      const minSize = this.bootstrapService.getBreakpointBoundary(breakpoint);
      if (minSize) {
        if (this.responsivity.widthFrom === 'table') {
          return width <= (minSize + this.responsivity.leeway);
        } else {
          return width <= minSize;
        }
      } else {
        this.logger.error('Breakpoint with name \'' + this.responsivity + '\' does not exist');
        return false;
      }
    } else {
      return false;
    }
  }

  onLimitChange(value: string) {
    const limit = parseInt(value, 10);
    this.limit = limit;
    this.table.limit = limit;
    this.table.onFooterPage({page: 1});
    this.userProfileService.updateCurrentUserRowsPerPagePreferences(limit).subscribe(_result => {
      // Ignore - need a subscription as otherwise http request is not made
    });
  }

  loadFirstPage(extras?: PageEventExtras): void {
    this.offset = 0;
    this.table.offset = 0;
    // Cannot call this.table.onFooterPage({page: 1}) here as the table may not have been initialised, and the offsite calculatipon
    // that DatatableComponent does in it's getter for the offset variable results in NaN
    this.pageEvent({offset: this.offset, limit: this.limit}, extras);
  }

  refreshPageOnDelete(rowsDeleted = 1, extras?: PageEventExtras): void {
    if (this.externalPaging) {
      if (this.rows && this.offset != null && this.limit != null) {
        let offset = this.offset;
        if (offset > 0 && this.rows.length <= rowsDeleted) {
          const newCount = this.count - rowsDeleted;
          if (offset * this.limit >= newCount) {
            const lastPageIndex = Math.max(Math.ceil(newCount / this.limit) - 1, 0);
            offset = Math.min(offset, lastPageIndex);
          }
        }
        this.pageEvent({offset: offset, limit: this.limit}, extras);
      } else {
        this.logger.error('refreshPageOnDelete() invalid row, offset or limit to refresh page');
      }
    } else {
      this.logger.error('refreshPageOnDelete() ignoring the refresh page request as externalPaging is not enabled');
    }
  }

  copyEvent(event: ClipboardEvent) {
    const selection = document ? document.getSelection() : null;
    if (selection && !selection.isCollapsed) {
      let startNode: Node;
      let startNodeOffset: number;
      let endNode: Node;
      let endNodeOffset: number;
      if (selection.rangeCount !== undefined && selection.rangeCount !== null && selection.rangeCount > 1) {
        const firstRange = selection.getRangeAt(0);
        const lastRange = selection.getRangeAt(selection.rangeCount - 1);
        startNode = firstRange.startContainer;
        startNodeOffset = firstRange.startOffset;
        endNode = lastRange.endContainer;
        endNodeOffset = lastRange.endOffset;
      } else {
        startNode = selection.anchorNode;
        startNodeOffset = selection.anchorOffset;
        endNode = selection.focusNode;
        endNodeOffset = selection.focusOffset;
      }

      if (startNode && endNode && startNode !== endNode && startNode.parentElement && endNode.parentElement) {
        const cellNodeName = 'datatable-body-cell';
        const headerCellNodeName = 'datatable-header-cell';
        const rowNodeName = 'datatable-body-row';
        const tableHeaderNodeName = 'datatable-header';
        const tableBodyNodeName = 'datatable-body';

        const startCellElement = this.findParentElementWithName(startNode.parentElement, cellNodeName);
        const endCellElement = this.findParentElementWithName(endNode.parentElement, cellNodeName);

        let headerElement: Element;
        let headerCellCount: number;
        let headerCellStartIndex: number;
        let headerCellEndIndex: number;
        let tableBodyElement: Element;
        let rowElements: HTMLCollectionOf<Element>;
        let rowCellCount: number;
        let startRowIndex: number;
        let endRowIndex: number;
        let startCellIndex: number;
        let endCellIndex: number;

        // If we cannot find the start row element, check for the table header
        if (startCellElement) {
          const startRowElement = this.findParentElementWithName(startCellElement, rowNodeName);
          rowCellCount = startRowElement.getElementsByTagName(cellNodeName).length;
          tableBodyElement = this.findParentElementWithName(startRowElement, tableBodyNodeName);
          rowElements = tableBodyElement.getElementsByTagName(rowNodeName);
          startRowIndex = this.findElementIndexInCollection(rowElements, startRowElement);
          startCellIndex = this.findElementIndex(startRowElement, cellNodeName, startCellElement);
        } else {
          const headerCell = this.findParentElementWithName(startNode.parentElement, headerCellNodeName);
          if (headerCell) {
            headerElement = this.findParentElementWithName(headerCell, tableHeaderNodeName);
            headerCellCount = headerElement.getElementsByTagName(headerCellNodeName).length;
            headerCellStartIndex = this.findElementIndex(headerElement, headerCellNodeName, headerCell);
          } else {
            // The selection does not seem to be inside ACE Table. Let the browser handle the event
            return;
          }
        }

        if (endCellElement) {
          // Table Body covered
          const endRowElement = this.findParentElementWithName(endCellElement, rowNodeName);
          if (rowCellCount === undefined || rowCellCount === null) {
            rowCellCount = endRowElement.getElementsByTagName(cellNodeName).length;
          }

          if (!tableBodyElement) {
            tableBodyElement = this.findParentElementWithName(endRowElement, tableBodyNodeName);
            rowElements = tableBodyElement.getElementsByTagName(rowNodeName);
          }
          endRowIndex = this.findElementIndexInCollection(rowElements, endRowElement);
          endCellIndex = this.findElementIndex(endRowElement, cellNodeName, endCellElement);

          if (headerCellStartIndex >= 0) {
            // The selection started in the header and ended in the table body, record the actual last cell in the header as the last
            // selected cell in the header, and the actual first cell in the table body to  be the first selected cell in the table body
            headerCellEndIndex = headerCellCount - 1;
            startRowIndex = 0;
            startCellIndex = 0;
          } else if (startRowIndex > endRowIndex) {
            // Backwards selection, switch start and end to allow the rest of the code to assume that the selection is not backwards
            [startRowIndex, endRowIndex] = [endRowIndex, startRowIndex];
            [startCellIndex, endCellIndex] = [endCellIndex, startCellIndex];
            [startNode, endNode] = [endNode, startNode];
            [startNodeOffset, endNodeOffset] = [endNodeOffset, startNodeOffset];
          } else if (startRowIndex === endRowIndex && startCellIndex > endCellIndex) {
            // Backwards selection on a single row in the table body, switch start and end to allow the rest of the code to assume that
            // the selection is not backwards
            [startCellIndex, endCellIndex] = [endCellIndex, startCellIndex];
            [startNode, endNode] = [endNode, startNode];
            [startNodeOffset, endNodeOffset] = [endNodeOffset, startNodeOffset];
          }
        } else {
          const headerCell = this.findParentElementWithName(endNode.parentElement, headerCellNodeName);
          if (headerCell) {
            if (headerCellStartIndex >= 0) {
              if (headerElement) {
                headerCellEndIndex = this.findElementIndex(headerElement, headerCellNodeName, headerCell);
                if (headerCellStartIndex > headerCellEndIndex) {
                  // Backwards selection, switch start and end to allow the rest of the code to assume that the selection is not backwards
                  [headerCellStartIndex, headerCellEndIndex] = [headerCellEndIndex, headerCellStartIndex];
                  [startNode, endNode] = [endNode, startNode];
                  [startNodeOffset, endNodeOffset] = [endNodeOffset, startNodeOffset];
                }
              } else {
                this.logger.error('Header start index initialised but no header element found');
                return;
              }
            } else if (startRowIndex >= 0 && startCellIndex >= 0) {
              // Selection ended in the header, when the start was in the body. Switch start and end so that the rest of the code can assume
              // the selection was not made backwards
              headerElement = this.findParentElementWithName(headerCell, tableHeaderNodeName);
              headerCellCount = headerElement.getElementsByTagName(headerCellNodeName).length;
              headerCellStartIndex = this.findElementIndex(headerElement, headerCellNodeName, headerCell);
              // Process the header to it's last cell (as the selection continues in the body)
              headerCellEndIndex = headerCellCount - 1;

              // Switch start and end, as we need the start to be in the header, and the end in the body
              endRowIndex = startRowIndex;
              endCellIndex = startCellIndex;
              startRowIndex = 0;
              startCellIndex = 0;
              [startNode, endNode] = [endNode, startNode];
              [startNodeOffset, endNodeOffset] = [endNodeOffset, startNodeOffset];
            } else {
              this.logger.error('The selected row and cell are expected to be initialised', startRowIndex, startCellIndex);
              return;
            }
          } else {
            // The selection does not seem to be inside ACE Table. Let the browser handle the event
            return;
          }
        }

        // Create clipboard data
        let clipboardData = '';

        const processHeader = headerCellStartIndex >= 0;
        const processBody = startRowIndex >= 0;

        if (processHeader) {
          // Process header
          // Pad if we will be processing the body and the first selected cell in the header is not the actual first cell in the header
          if (processBody && headerCellStartIndex > 0) {
            clipboardData += '\t'.repeat(headerCellStartIndex);
          }

          clipboardData += startNode.nodeValue.substring(startNodeOffset).trim();
          if (processBody) {
            // Will be processing the table body
            clipboardData += this.convertRowToClipboardString(headerElement, headerCellNodeName, headerCellStartIndex + 1, headerCellEndIndex, '\n');
          } else {
            clipboardData += this.convertRowToClipboardString(headerElement, headerCellNodeName, headerCellStartIndex + 1, headerCellEndIndex - 1);
            clipboardData += '\t' + endNode.nodeValue.substring(0, endNodeOffset).trim();
            clipboardData += '\n';
          }
        }

        if (processBody) {
          // Process table body
          const singleRow = startRowIndex === endRowIndex;
          let lastCellIndex;
          let lineTerminator;
          if (singleRow) {
            lastCellIndex = endCellIndex - 1;
            lineTerminator = '';
          } else {
            lastCellIndex = rowCellCount - 1;
            lineTerminator = '\n';
          }

          // Add First Row
          // Pad the data if processing more than one row and the first selected cell is not the first cell in it's row
          if (!singleRow && startCellIndex > 0) {
            clipboardData += '\t'.repeat(startCellIndex);
          }

          if (processHeader) {
            clipboardData += this.convertRowToClipboardString(rowElements[startRowIndex], cellNodeName, startCellIndex, lastCellIndex, lineTerminator);
          } else {
            clipboardData += startNode.nodeValue.substring(startNodeOffset).trim();
            clipboardData += this.convertRowToClipboardString(rowElements[startRowIndex], cellNodeName, startCellIndex + 1, lastCellIndex, lineTerminator);
          }

          // Add Middle Rows
          for (let i = startRowIndex + 1; i < endRowIndex; i++) {
            clipboardData += this.convertRowToClipboardString(rowElements[i], cellNodeName, 0, rowCellCount - 1, '\n');
          }

          if (!singleRow) {
            // Add Last Row
            clipboardData += this.convertRowToClipboardString(rowElements[endRowIndex], cellNodeName, 0, endCellIndex - 1);
          }

          // Use the data in the copy event for the last cell, as the user may have selected only part of the cell's text,
          // i.e. not all of the text in the cell
          if (endCellIndex > 0) {
            clipboardData += '\t';
          }

          if (endNode != null && endNode.nodeValue != null) {
            clipboardData += endNode.nodeValue.substring(0, endNodeOffset).trim();
          } else if (endNodeOffset !== 0) {
            this.logger.error('Cannot copy the last selected cell content as the endNode or endNode value is null. endNode', endNode,
              'endNode.nodeValue', endNode && endNode.nodeValue, 'endNodeOffset', endNodeOffset);
          }
          clipboardData += '\n';
        }

        event.clipboardData.setData('text/plain', clipboardData);
        event.preventDefault();
      }
    }
  }

  /**
   * ngx-datable does not allow to supply a custom spinner template. It creates a simple div, which can be styled. This
   * is not flexible enough for the spinner that is needed, as the bootstrap spinner needs 3 divs. So have to create
   * it dynamically using the DOM API :(
   * @private
   */
  private checkLoadingIndicatorOverBodyOnly(): void {
    // Only need to do anything if loadingIndicatorOverBodyOnly has been enabled (false by default) and loading is in progress
    if (this.loadingIndicatorOverBodyOnly && this.loading) {
      const tableLoadingIndicatorDiv = document.querySelector('#' + this.tableHTMLId + ' .datatable-body .progress-linear > .container > .bar');
      if (tableLoadingIndicatorDiv) {
        const spinner = tableLoadingIndicatorDiv.getElementsByClassName(this.TABLE_SPINNER_CSS_CLASS_NAME);
        // Only need to create the spinner HTML if it does not already exist
        if (!spinner?.length) {
          const spinner = document.createElement('div');
          spinner.className = this.TABLE_SPINNER_CSS_CLASS_NAME + ' ' + this.TABLE_ON_TOP_CSS_CLASS_NAME;
          const bounce1 = document.createElement('div');
          bounce1.className = this.TABLE_SPINNER_BOUNCE_CSS_CLASS_NAME_PREFIX + '1';
          spinner.appendChild(bounce1);
          const bounce2 = document.createElement('div');
          bounce2.className = this.TABLE_SPINNER_BOUNCE_CSS_CLASS_NAME_PREFIX + '2';
          spinner.appendChild(bounce2);
          const bounce3 = document.createElement('div');
          bounce3.className = this.TABLE_SPINNER_BOUNCE_CSS_CLASS_NAME_PREFIX + '3';
          spinner.appendChild(bounce3);
          tableLoadingIndicatorDiv.appendChild(spinner);
        }
      }
    }
  }

  private rebuildDefaultColumnSortDirsIfNeeded() {
    if (this.rebuildDefaultColumnSortDirs) {
      this.columnDefaultSortDirs = new Map();
      (this.columns || []).forEach(c => {
        if (c && c.defaultSortDir) {
          this.columnDefaultSortDirs.set(c.$$id, c.defaultSortDir);
        }
      });
      this.rebuildDefaultColumnSortDirs = false;
    }
  }

  private findParentElementWithName(element: Element, nodeName): Element {
    while (element && element.nodeName.toLocaleLowerCase() !== nodeName.toLowerCase()) {
      element = element.parentElement;
    }
    return element;
  }

  private findElementIndex(parent: Element, childTagName: string, elementToFind: Element): number {
    return this.findElementIndexInCollection(parent.getElementsByTagName(childTagName), elementToFind);
  }

  private findElementIndexInCollection(elements: HTMLCollectionOf<Element>, elementToFind: Element): number {
    for (let i = 0; i < elements.length; i++) {
      const childElement = elements[i];
      if (childElement === elementToFind) {
        return i;
      }
    }
    return -1;
  }

  private convertRowToClipboardString(rowElement: Element, childTagName: string, startIndex: number, endIndex: number, rowTerminator = ''): string {
    let data = '';
    const childElements = rowElement.getElementsByTagName(childTagName);
    for (let i = startIndex; i <= endIndex; i++) {
      const childElement = childElements[i];
      if (childElement) {
        if (i !== 0) {
          data += '\t';
        }
        const text = (<HTMLElement>childElement).innerText;
        if (text) {
          data += text.trim();
        }
      }
    }
    data += rowTerminator;
    return data;
  }

  get shouldShowJumpToPage(): boolean {
    return !this.simplePaging && this.showJumpToPage && this.count > 0 && this.maxPageNum > this.MAX_PAGER_SIZE;
  }

  gotoPage(): void {
    const pageNum = +this.jumpToPage.get('pageNumber').value;
    if (0 <= pageNum && pageNum <= this.maxPageNum && pageNum !== 0 && pageNum !== this.currentPage) {
      this.table.onFooterPage({page: pageNum});
    }
  }

  getJumpToPageTooltip(): string {
    // only show tooltip when error presents.
    return this.jumpToPage.getError('invalidPageNumber', 'pageNumber') || '';
  }

  private listenForTableResizeEvents(): void {
    new ResizeObservable(this.tableWrapper.nativeElement)
      .pipe(
        debounceTime(200),
        takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.logger.debug('Table resized');
        this.resizeTableIfNeeded();
      });
  }
}
